import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ShieldPlus, Plus, Pencil, Power, Wallet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { InsurerCCDialog } from '../components/InsurerCCDialog';
import { InvoiceFormatDialog } from '../components/InvoiceFormatDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IVA_CONDITIONS = [
  { v: 'consumidor_final', label: 'Consumidor Final' },
  { v: 'monotributo', label: 'Monotributo' },
  { v: 'responsable_inscripto', label: 'Responsable Inscripto' },
  { v: 'exento', label: 'Exento' },
];

function userCan(user, mod, act) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[mod]?.[act] === true;
}

const empty = {
  name: '', legal_name: '', tax_id: '', iva_condition: 'responsable_inscripto',
  fiscal_address: '', billing_modality: 'individual', talonario: 'proforma',
  receptor: 'insurer', service_ids: [], affiliate_types: [],
  is_third_party_payer: false, notes: '',
};

function HealthInsurers() {
  const { user } = useOutletContext();
  const [list, setList] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [dialog, setDialog] = useState({ open: false, data: empty });
  const [ccDialog, setCcDialog] = useState({ open: false, insurer: null });
  const [fmtDialog, setFmtDialog] = useState({ open: false, insurer: null });
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [newType, setNewType] = useState('');

  const addType = () => {
    const t = newType.trim().replace(/\s+/g, ' ');
    if (!t) return;
    setDialog(prev => {
      const cur = prev.data.affiliate_types || [];
      if (cur.includes(t)) return prev;
      return { ...prev, data: { ...prev.data, affiliate_types: [...cur, t] } };
    });
    setNewType('');
  };

  const canCreate = userCan(user, 'servicios_residentes', 'crear');
  const canEdit = userCan(user, 'servicios_residentes', 'editar');

  const fetchAll = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/health-insurers?include_inactive=${showInactive}`, { withCredentials: true });
      setList(r.data || []);
    } catch (e) { toast.error('Error al cargar convenios'); }
  }, [showInactive]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/resident-services`, { withCredentials: true })
      .then(r => setServices((r.data || []).filter(x => x.is_active !== false)))
      .catch(() => setServices([]));
  }, []);

  // v2.29.0: deep-link desde /billing/debtors → abrir CC de la OS automáticamente
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const insurerId = params.get('open_cc');
    if (!insurerId || list.length === 0) return;
    const os = list.find(x => x.insurer_id === insurerId);
    if (!os) return;
    setCcDialog({ open: true, insurer: os });
    window.history.replaceState({}, '', '/health-insurers');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const d = dialog.data;
  const setF = (k, v) => setDialog(prev => ({ ...prev, data: { ...prev.data, [k]: v } }));
  const openNew = () => setDialog({ open: true, data: { ...empty } });
  const openEdit = (os) => setDialog({ open: true, data: { ...empty, ...os } });

  const save = async () => {
    if (!d.name || !d.legal_name || !d.tax_id) { toast.error('Completá nombre, razón social y CUIT'); return; }
    setSaving(true);
    try {
      const payload = {
        name: d.name, legal_name: d.legal_name, tax_id: d.tax_id, iva_condition: d.iva_condition,
        fiscal_address: d.fiscal_address || null, billing_modality: d.billing_modality,
        talonario: d.talonario, receptor: d.receptor || 'insurer',
        service_ids: d.service_ids || [], affiliate_types: d.affiliate_types || [],
        is_third_party_payer: !!d.is_third_party_payer,
        notes: d.notes || null,
      };
      if (d.insurer_id) await axios.put(`${BACKEND_URL}/api/health-insurers/${d.insurer_id}`, payload, { withCredentials: true });
      else await axios.post(`${BACKEND_URL}/api/health-insurers`, payload, { withCredentials: true });
      toast.success('Convenio guardado');
      setDialog({ open: false, data: empty });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); } finally { setSaving(false); }
  };

  const toggleActive = async (os) => {
    try {
      if (os.is_active) await axios.delete(`${BACKEND_URL}/api/health-insurers/${os.insurer_id}`, { withCredentials: true });
      else await axios.put(`${BACKEND_URL}/api/health-insurers/${os.insurer_id}`, { is_active: true }, { withCredentials: true });
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2"><ShieldPlus className="w-7 h-7 text-teal-500" /> Convenios</h1>
        {canCreate && <Button onClick={openNew} data-testid="new-insurer-btn"><Plus className="w-4 h-4 mr-2" />Nuevo convenio</Button>}
      </div>

      <Card>
        <CardHeader>
          <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="show-inactive" />
            Mostrar inactivos
          </label>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Convenio</TableHead><TableHead>CUIT</TableHead><TableHead>Cond. IVA</TableHead>
              <TableHead>Se factura a</TableHead><TableHead>Conceptos del convenio</TableHead>
              <TableHead>Modalidad</TableHead><TableHead>Talonario</TableHead><TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Sin convenios</TableCell></TableRow> :
                list.map(os => (
                  <TableRow key={os.insurer_id} data-testid={`insurer-row-${os.insurer_id}`}>
                    <TableCell className="font-medium">{os.name}<div className="text-xs text-muted-foreground">{os.legal_name}</div>
                      {(os.affiliate_types || []).length > 0 && (
                        <div className="text-[11px] text-teal-700 mt-0.5" data-testid={`insurer-affiliates-${os.insurer_id}`}>
                          Agrupa por tipo de afiliado ({(os.affiliate_types || []).length})
                        </div>
                      )}
                      {(os.invoice_templates || []).length > 0 && (
                        <div className="text-[11px] text-slate-600 mt-0.5" data-testid={`insurer-templates-${os.insurer_id}`}>
                          {(os.invoice_templates || []).length} modelo(s) de texto en la factura
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{os.tax_id}</TableCell>
                    <TableCell className="text-xs">{IVA_CONDITIONS.find(c => c.v === os.iva_condition)?.label || os.iva_condition}</TableCell>
                    <TableCell className="text-xs">
                      {os.receptor === 'resident'
                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Al residente</span>
                        : <span className="inline-flex items-center gap-1 text-indigo-700"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />A la obra social</span>}
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`insurer-services-${os.insurer_id}`}>
                      {(os.service_ids || []).length === 0
                        ? <span className="text-amber-700">— sin vincular —</span>
                        : (os.service_ids || []).map(sid => {
                            const sv = services.find(x => x.service_id === sid);
                            return <Badge key={sid} variant="outline" className="mr-1 mb-1 text-[10px]">{sv ? `${sv.code ? sv.code + ' · ' : ''}${sv.name}` : sid}</Badge>;
                          })}
                    </TableCell>
                    <TableCell><Badge variant="outline" className={os.billing_modality === 'consolidada' ? 'border-purple-400 text-purple-700' : ''}>{os.billing_modality === 'consolidada' ? 'Consolidada (1 FC)' : 'Individual'}</Badge></TableCell>
                    <TableCell className="text-xs uppercase">{os.talonario}</TableCell>
                    <TableCell>{os.is_active ? <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge> : <Badge className="bg-slate-200 text-slate-600">Inactivo</Badge>}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => setCcDialog({ open: true, insurer: os })} data-testid={`cc-${os.insurer_id}`} title="Cuenta Corriente"><Wallet className="w-4 h-4 text-indigo-600" /></Button>
                      {canEdit && <Button variant="ghost" size="icon" onClick={() => setFmtDialog({ open: true, insurer: os })} data-testid={`format-${os.insurer_id}`} title="Formato de factura (modelos de texto)"><FileText className="w-4 h-4 text-teal-600" /></Button>}
                      {canEdit && <Button variant="ghost" size="icon" onClick={() => openEdit(os)} data-testid={`edit-${os.insurer_id}`}><Pencil className="w-4 h-4" /></Button>}
                      {canEdit && <Button variant="ghost" size="icon" className={os.is_active ? 'text-red-600' : 'text-emerald-600'} onClick={() => toggleActive(os)} title={os.is_active ? 'Desactivar' : 'Activar'}><Power className="w-4 h-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, data: empty })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{d.insurer_id ? 'Editar' : 'Nuevo'} convenio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre del convenio *</Label><Input value={d.name} onChange={(e) => setF('name', e.target.value)} data-testid="insurer-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Razón social *</Label><Input value={d.legal_name} onChange={(e) => setF('legal_name', e.target.value)} /></div>
              <div><Label>CUIT *</Label><Input value={d.tax_id} onChange={(e) => setF('tax_id', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Condición IVA</Label>
                <select value={d.iva_condition} onChange={(e) => setF('iva_condition', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm">
                  {IVA_CONDITIONS.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
                </select></div>
              <div><Label>Domicilio fiscal</Label><Input value={d.fiscal_address} onChange={(e) => setF('fiscal_address', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Modalidad de facturación</Label>
                <select value={d.billing_modality} onChange={(e) => setF('billing_modality', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="insurer-modality">
                  <option value="individual">Individual (1 FC por residente)</option>
                  <option value="consolidada">Consolidada (1 FC para todos)</option>
                </select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Se factura a *</Label>
                <select value={d.receptor || 'insurer'} onChange={(e) => setF('receptor', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="insurer-receptor">
                  <option value="insurer">🏥 A la obra social</option>
                  <option value="resident">🟢 Al residente / familiar</option>
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">Los convenios particulares se facturan al residente.</p>
              </div>
              <div><Label>Talonario</Label>
                <select value={d.talonario} onChange={(e) => setF('talonario', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm">
                  <option value="proforma">Proforma</option>
                  <option value="arca">ARCA</option>
                </select></div>
            </div>
            <div className="rounded-md border p-3 space-y-2" data-testid="insurer-services-block">
              <div>
                <Label>Conceptos de este convenio</Label>
                <p className="text-[11px] text-muted-foreground">Los residentes de este convenio facturan estos conceptos prestacionales. Los conceptos de otros convenios quedan bloqueados en su ficha.</p>
              </div>
              <div className="grid grid-cols-2 gap-1 max-h-44 overflow-y-auto">
                {services.filter(s => s.service_class === 'prestacional').map(s => (
                  <label key={s.service_id} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
                    <input type="checkbox" className="h-3.5 w-3.5"
                           checked={(d.service_ids || []).includes(s.service_id)}
                           onChange={() => setF('service_ids', (d.service_ids || []).includes(s.service_id)
                             ? (d.service_ids || []).filter(x => x !== s.service_id)
                             : [...(d.service_ids || []), s.service_id])}
                           data-testid={`insurer-service-${s.service_id}`} />
                    <span>{s.code ? `${s.code} · ` : ''}{s.name}</span>
                    <span className="text-muted-foreground">$ {Number(s.current_price || 0).toLocaleString('es-AR')}</span>
                  </label>
                ))}
                {services.filter(s => s.service_class === 'prestacional').length === 0 && (
                  <span className="text-xs text-amber-700">No hay conceptos prestacionales en el catálogo. Marcá un concepto como «prestacional» en Catálogo Servicios.</span>
                )}
              </div>
            </div>
            <div className="rounded-md border p-3 space-y-2" data-testid="insurer-affiliate-block">
              <div>
                <Label>Tipos de afiliado</Label>
                <p className="text-[11px] text-muted-foreground">
                  Si el convenio los usa, la corrida masiva emite <b>una factura por cada tipo de afiliado</b> y
                  cada residente tiene que tener el suyo cargado en la ficha. Dejalo vacío si el convenio no los pide.
                </p>
              </div>
              <div className="flex flex-wrap gap-1">
                {(d.affiliate_types || []).length === 0 && (
                  <span className="text-xs text-muted-foreground">Sin tipos de afiliado (factura agrupada como hasta ahora).</span>
                )}
                {(d.affiliate_types || []).map(t => (
                  <Badge key={t} variant="outline" className="text-[11px] gap-1" data-testid={`affiliate-type-${t}`}>
                    {t}
                    <button type="button" className="text-red-600 font-bold px-0.5"
                            onClick={() => setF('affiliate_types', (d.affiliate_types || []).filter(x => x !== t))}
                            data-testid={`affiliate-type-del-${t}`}>×</button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newType} onChange={(e) => setNewType(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addType(); } }}
                       placeholder="Ej: Voluntarios individuales" data-testid="affiliate-type-input" />
                <Button type="button" variant="outline" onClick={addType} data-testid="affiliate-type-add">Agregar</Button>
              </div>
            </div>
            <div><Label>Notas</Label><Textarea rows={2} value={d.notes} onChange={(e) => setF('notes', e.target.value)} /></div>
            <div className="rounded-md border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-1" data-testid="insurer-third-party-block">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" className="h-4 w-4"
                       checked={!!d.is_third_party_payer}
                       onChange={(e) => setF('is_third_party_payer', e.target.checked)}
                       data-testid="insurer-is-third-party" />
                <span><b>Pagador de Terceros</b> (Curaduría Oficial y similares)</span>
              </label>
              <p className="text-[11px] text-muted-foreground pl-6">
                Cuando está marcado, este convenio agrupa en su Cuenta Corriente facturas
                emitidas a residentes cubiertos por él (FC-ARCA con CUIT del residente).
                No recibe FC-ARCA-CONVENIO propias. Se selecciona por residente en la
                solapa Facturación de su ficha.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, data: empty })}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="save-insurer-btn">{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InsurerCCDialog
        open={ccDialog.open}
        onOpenChange={(o) => !o && setCcDialog({ open: false, insurer: null })}
        insurer={ccDialog.insurer}
      />

      <InvoiceFormatDialog
        open={fmtDialog.open}
        insurer={fmtDialog.insurer}
        services={services}
        onClose={() => setFmtDialog({ open: false, insurer: null })}
        onSaved={fetchAll}
      />
    </div>
  );
}

export default HealthInsurers;
